import {Card, FormControl, FormField} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';

import {Path, UseFormReturn, useWatch} from 'react-hook-form';

import {isFalse, isTrue} from 'ramda-adjunct';

import {SeriesResponseBody} from '@dms/api/core';
import {DefaultPaymentType} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromSeriesList} from '@dms/settings/shared';
import {useDocumentTemplates} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../../types/WarehouseDirectSaleVariantsDetailFormSchema';

interface PaymentCardProps extends TestIdProps {
  title: string;
  paymentType: DefaultPaymentType;
  path: 'cardPayment' | 'cashPayment' | 'bankPayment';
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  docSeriesId: Path<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  invoiceTemplateId: Path<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  seriesList: SeriesResponseBody[] | Nullish;
}

export function PaymentCard(props: PaymentCardProps) {
  const isPaymentTypeEnabled = useWatch({
    control: props.control,
    name: `documents.${props.path}.isEnable`,
  });
  const defaultPaymentType = useWatch({name: 'documents.defaultPaymentType'});

  const [getTemplates] = useDocumentTemplates();
  const {options: invoiceTemplatesOptions} = getTemplates('invoice');

  const handlePaymentTypeToggle = (value: boolean) => {
    const paymentTypeIsDefault = props.paymentType === defaultPaymentType;

    props.formApi.setValue(`documents.${props.path}.isEnable`, value, {shouldDirty: true});

    if (isFalse(value)) {
      clearPaymentFields(props.formApi);

      if (paymentTypeIsDefault) {
        props.formApi.setValue('documents.defaultPaymentType', null);
      }
    }
  };

  const clearPaymentFields = (
    formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>
  ) => {
    const fieldsToClear = [
      'invoice.docSeriesId',
      'invoice.templateId',
      'deposit.docSeriesId',
      'invoiceCorrection.docSeriesId',
      'taxDocument.docSeriesId',
      'taxDocumentCorrection.docSeriesId',
      'invoiceEu.docSeriesId',
      'invoiceEuCorrection.docSeriesId',
      'invoiceOutsideEu.docSeriesId',
      'invoiceOutsideEuCorrection.docSeriesId',
    ];

    fieldsToClear.forEach((field) => {
      formApi.setValue(
        `documents.${props.path}.${field}` as Path<WarehouseDirectSaleVariantsDetailFormSchemaType>,
        null
      );
    });
  };

  return (
    <Card
      title={props.title}
      variant="inlineGrey"
      isExpanded={isTrue(isPaymentTypeEnabled)}
      control={{
        type: 'switch',
        value: isPaymentTypeEnabled,
        onChange: handlePaymentTypeToggle,
      }}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoice.templateId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceTemplate')}
              options={invoiceTemplatesOptions}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.deposit.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.proformaInvoiceDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoice.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceCorrectionDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.taxDocument.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.taxDocumentDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.taxDocumentCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.taxDocumentCorrectionDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceEu.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceEuDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceEuCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceEuCorrectionDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>

        <Grid columns={2}>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceOutsideEu.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceOutsideEuDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('invoiceTemplateId', props)}
            />
          </GridItem>
          <GridItem span={1}>
            <FormField
              isRequired={isPaymentTypeEnabled}
              isNotClearable={isPaymentTypeEnabled}
              control={props.control}
              name={`documents.${props.path}.invoiceOutsideEuCorrection.docSeriesId`}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceOutsideEuCorrectionDocumentSerie')}
              options={getOptionsFromSeriesList(props.seriesList, 'accounting/invoice')}
              data-testid={suffixTestId('docSeriesId', props)}
            />
          </GridItem>
        </Grid>
      </VStack>
    </Card>
  );
}
